<script setup lang="ts">
import type { OfferTermsContent } from '@promotion/types'
import type { HeroContent } from '@nimbus/types'
import type { StickyConfig } from '@promotion/components/Promotion-Terms.vue'
import { usePartner } from '#imports'

defineOptions({
    name: 'StickyTerms'
})

export interface StickyTermsProps {
    backgroundColor?: string
    title?: string
    isLandingPage?: boolean
    heroData?: HeroContent
    forceFullTerms?: boolean
    forceShortTerms?: boolean
    enableMultiple?: boolean
    stickyConfig?: StickyConfig
}
const props = defineProps<StickyTermsProps>()

const { isFromPartner } = usePartner()
const { signupBundles, defaultBundle } = useSignupBundles()

const showFullTerms = computed(() => {
    if (props.forceShortTerms) return false

    return props.forceFullTerms || isFromPartner.value
})

const terms = computed<OfferTermsContent[]>(() => {
    const termsArray: OfferTermsContent[] = []

    if (props.heroData) {
        if (props.heroData.short_terms || props.heroData.long_terms) {
            let heroTerms1 = props.heroData.short_terms
            let heroTerms2 = props.heroData.short_terms_2

            if (showFullTerms.value) {
                heroTerms1 = props.heroData?.long_terms || props.heroData?.short_terms
                heroTerms2 = props.heroData?.long_terms_2 || props.heroData?.short_terms_2
            }

            if (heroTerms1) termsArray.push({ text: heroTerms1 })
            if (heroTerms2) termsArray.push({ text: heroTerms2 })
        }

        return termsArray
    }

    if (!termsArray.length && signupBundles.value?.length) {
        if (props.enableMultiple) {
            signupBundles.value.forEach((bundle) => {
                const relevantTerms = showFullTerms.value
                    ? bundle.longTerms
                    : bundle.shortTerms

                termsArray.push({ label: bundle.label, title: bundle.title, text: relevantTerms })
            })

            return termsArray
        }

        if (!defaultBundle.value) return termsArray

        let relevantTerms = showFullTerms.value
            ? defaultBundle.value.longTerms
            : defaultBundle.value.shortTerms

        // Terms are combined on landingPages to make sure we are total and absolutly compliant!-_-!
        if (props.isLandingPage && !props.forceFullTerms) {
            relevantTerms = showFullTerms.value
                ? `${defaultBundle.value.shortTerms}<br><br>${defaultBundle.value?.longTerms}`
                : defaultBundle.value.shortTerms
        }

        termsArray.push({ label: defaultBundle.value.label, text: relevantTerms })
    }

    return termsArray
})
</script>

<template>
    <PromotionTerms
        v-if="terms.length"
        class="container sticky-terms"
        :class="{ 'sticky-terms--landing-page': isLandingPage }"
        :terms="terms"
        :show-title="!showFullTerms"
        :title="title"
        :type="showFullTerms ? 'long' : 'short'"
        :sticky-config="stickyConfig"
        style="margin-top: 0"
    />
</template>

<style scoped lang="scss">
.sticky-terms {
    z-index: 6;

    &--landing-page {
        padding-bottom: 1rem;
        margin-left: auto;
        margin-right: auto;
        max-width: 1024px;
        padding-left: 15px;
        padding-right: 15px;

        :deep(.offer-terms__text b) {
            display: block;
            font-weight: bold;
        }

        :deep(.offer-terms__text) {
            line-height: 1.4;
        }

        :deep(h2) {
            font-size: 16px;
            font-weight: 700;
        }

        :deep(h3) {
            font-size: 14px;
            font-weight: 700;
        }
    }
}
</style>
